// Campaign recommendations «Προτάσεις» (handoff v08 requirement 24). The Campaign Center proposes 1–3 campaigns at a
// time, each one explainable: who (a consent-filtered audience count), what (an existing theme and its draft from the
// theme library), how much (a contact goal), when (a schedule from the theme's sequence) and how it will be measured
// (KPIs: contacts, positive replies, meetings, conversion). Nothing happens without the advisor: approval creates the
// campaign through saveCampaign + prepareCampaign (the same consent rules freeze the same audience) and nothing is
// ever sent — launching (advisor tasks) stays a separate, explicit step on the campaign.
//
// INPUTS USED (RECOMMENDATION_INPUTS — also shown on the screen):
//   segments  the existing smart segments (runSegment), including the lawful protection-gap segments already in the
//             product; only segments named by a library theme are proposed
//   consent   audienceFor(): marketing consent, withdrawal, channel consent, suppression list, «do not call», contact detail
//   history   campaign_activities (contacted / responded / appointment / opted out), open campaign tasks, earlier
//             campaigns of the same theme, campaigns already running
//   season    a fixed calendar table (SEASONAL_THEMES) — dates only, no market claim
//   market    market notes the ADVISOR stored with a source and a date (setting campaign.marketNotes); never generated
//   own_rates the advisor's own past campaign rates, only for KPI targets and only with ≥ KPI_MIN_SAMPLE contacts
// NOT USED (NOT_USED_INPUTS): health conditions or answers, smoker status, claims, medical documents or requirements,
// notes and free text, income or tax details beyond the existing segments, ages beyond the existing segments.
import type { Changeset, Put, WorkspaceView } from './changeset.ts';
import { DomainError, clean, opt, type Ctx } from './cmd.ts';
import { addDays, diffDays, isDay, weekday, type Day } from './dates.ts';
import { CAMPAIGN_THEMES, themeOf, themeText, type CampaignTheme } from './campaign-themes.ts';
import { prepareCampaign, saveCampaign, type AudiencePreview } from './campaigns.ts';
import { SEGMENT_KEYS, audienceFor, runSegment, type Channel, type SegmentKey } from './segments.ts';
import { getSetting, saveSetting } from './services.ts';
import type { RowTypes, TableName } from './schema.ts';

export const RECOMMENDATION_INPUTS = ['segments', 'consent', 'history', 'season', 'market', 'own_rates'] as const;
export const NOT_USED_INPUTS = ['health', 'smoker', 'claims', 'medical_documents', 'notes', 'finances'] as const;
/** the segments that touch health insurance at all: they exist in the product already and test only for a MISSING health
 *  policy (never a condition) — the engine may use them because the themes use them, nothing else health-related */
export const HEALTH_COVER_SEGMENTS: readonly SegmentKey[] = ['no_medical_recorded', 'life_no_medical', 'gap_health'];
export const MARKET_NOTES_KEY = 'campaign.marketNotes';
export const DISMISSED_KEY = 'campaign.recommendations.dismissed';
export const KPI_MIN_SAMPLE = 20;
/** people contacted by any campaign within this many days are counted apart (not contacted again so soon) */
export const COOL_OFF_DAYS = 30;
/** a theme run (completed or launched) within this many days is not proposed again */
export const REPEAT_DAYS = 90;
/** a market note older than this is no longer cited */
export const MARKET_NOTE_DAYS = 180;
export const REC_CHANNELS: readonly Channel[] = ['whatsapp', 'email', 'sms', 'phone'];

/** fixed calendar seasons (months 1–12) — the reason shown is the calendar, never a market statement */
export const SEASONAL_THEMES: Record<string, { months: number[]; reason: string }> = {
  tax_year_opportunity: { months: [10, 11, 12], reason: 'tax_year_end' },
  future_of_my_child: { months: [8, 9], reason: 'school_year' },
  new_parents: { months: [8, 9], reason: 'school_year' },
  policy_review_month: { months: [1, 2], reason: 'new_year' },
  family_on_the_road: { months: [6, 7], reason: 'summer_travel' },
  home_earthquake_check: { months: [3, 4], reason: 'spring_home' },
  retirement_gap: { months: [1], reason: 'new_year' },
};

export interface MarketNote { id: string; title: string; source: string; url?: string | null; date: Day; themes: string[]; summary?: string | null; addedAt: string }
export function marketNotes(v: WorkspaceView): MarketNote[] { return getSetting<MarketNote[]>(v, MARKET_NOTES_KEY, []).filter((n) => n && n.id); }
/** a note counts only with a source and a dated publication (≤ today); it is quoted as written, never extended */
export function saveMarketNote(v: WorkspaceView, input: { id?: string; title: string; source: string; url?: string | null; date: Day; themes: string[]; summary?: string | null }, ctx: Ctx): Changeset {
  const title = clean(input.title), source = clean(input.source);
  if (!title) throw new DomainError('market_note.title_required');
  if (!source) throw new DomainError('market_note.source_required');
  if (!isDay(input.date) || input.date > ctx.today) throw new DomainError('market_note.date_invalid');
  const url = opt(input.url);
  if (url && !/^https?:\/\/\S+$/i.test(url)) throw new DomainError('market_note.url_invalid');
  const themes = [...new Set(input.themes ?? [])].filter((k) => themeOf(k));
  if (!themes.length) throw new DomainError('market_note.theme_required');
  const list = marketNotes(v);
  const id = input.id ?? `mn-${ctx.now.replace(/\D/g, '').slice(0, 14)}-${list.length + 1}`;
  const note: MarketNote = { id, title, source, url, date: input.date, themes, summary: opt(input.summary), addedAt: list.find((n) => n.id === id)?.addedAt ?? ctx.now };
  const next = list.some((n) => n.id === id) ? list.map((n) => (n.id === id ? note : n)) : [...list, note];
  return { ...saveSetting(v, MARKET_NOTES_KEY, next, ctx), action: 'campaign.market_note.save', summary: title };
}
export function deleteMarketNote(v: WorkspaceView, id: string, ctx: Ctx): Changeset {
  return { ...saveSetting(v, MARKET_NOTES_KEY, marketNotes(v).filter((n) => n.id !== id), ctx), action: 'campaign.market_note.delete', summary: id };
}

export interface RecReason { key: string; params: Record<string, string | number> }
export interface RecStep { day: Day; action: CampaignTheme['sequence'][number]['action'] }
export interface CampaignKpis {
  contacts: number;
  /** targets from the advisor's own history (null = not enough history: measured from the first contacts) */
  positiveReplies: number | null; meetings: number | null; conversion: number | null;
  basis: 'own_history' | 'not_enough_history'; sample: number;
}
export interface CampaignRecommendation {
  key: string; themeKey: string; segmentKey: SegmentKey; channel: Channel;
  audience: { members: number; eligible: number; excluded: Record<string, number>; incomplete: number; recentlyContacted: number; fresh: number; population: number };
  /** per channel: how many would be eligible (so the advisor sees the effect of choosing another channel) */
  byChannel: Partial<Record<Channel, number>>;
  contactGoal: { total: number; perDay: number; days: number };
  schedule: { start: Day; end: Day; steps: RecStep[] };
  draft: { subject?: string; body: string };
  kpis: CampaignKpis;
  market: MarketNote[];
  season: string | null;
  reasons: RecReason[];
  score: number;
}

const OPEN_STATUSES = new Set(['draft', 'ready', 'active']);
const nextWorkingDay = (d: Day): Day => { let x = addDays(d, 1); while (weekday(x) === 0 || weekday(x) === 6) x = addDays(x, 1); return x; };
/** advance n working days (Mon–Fri) from d */
const workingDaysAfter = (d: Day, n: number): Day => { let x = d; for (let i = 0; i < n; i++) x = nextWorkingDay(x); return x; };

/** people contacted by any campaign within the cool-off window, or with an open campaign task */
function recentlyContacted(v: WorkspaceView, today: Day): Set<string> {
  const out = new Set<string>();
  for (const a of v.rows('campaign_activities')) if (a.personId && a.type === 'contacted' && diffDays(a.at.slice(0, 10), today) <= COOL_OFF_DAYS) out.add(a.personId);
  for (const t of v.rows('tasks')) if (t.origin === 'campaign' && t.status === 'open' && t.personId) out.add(t.personId);
  return out;
}
/** the advisor's own campaign history: distinct (campaign, person) contacted, responded, appointments, issued */
export function ownCampaignRates(v: WorkspaceView): { contacted: number; responded: number; appointments: number; issued: number } {
  const by = (type: string) => new Set(v.rows('campaign_activities').filter((a) => a.type === type && a.personId).map((a) => `${a.campaignId}:${a.personId}`)).size;
  return { contacted: by('contacted'), responded: by('responded'), appointments: by('appointment'), issued: by('issued') };
}
export function kpisFor(v: WorkspaceView, contacts: number): CampaignKpis {
  const r = ownCampaignRates(v);
  if (r.contacted < KPI_MIN_SAMPLE) return { contacts, positiveReplies: null, meetings: null, conversion: null, basis: 'not_enough_history', sample: r.contacted };
  return { contacts, positiveReplies: Math.round((contacts * r.responded) / r.contacted), meetings: Math.round((contacts * r.appointments) / r.contacted), conversion: Math.round((r.issued / r.contacted) * 1000) / 10, basis: 'own_history', sample: r.contacted };
}
export function draftFor(th: CampaignTheme, channel: Channel, locale: string): { subject?: string; body: string } {
  const x = themeText(th, locale);
  return channel === 'email' ? { subject: x.emailSubject, body: x.emailBody } : channel === 'phone' ? { body: x.callScript } : { body: x.whatsapp };
}
const dismissed = (v: WorkspaceView) => getSetting<Record<string, Day>>(v, DISMISSED_KEY, {});

/** One candidate per theme, explained; null when it should not be proposed (with the reason, for tests/diagnostics). */
export function evaluateTheme(v: WorkspaceView, th: CampaignTheme, today: Day, opts: { locale?: string; channel?: Channel; recent?: Set<string> } = {}): { rec: CampaignRecommendation | null; skip?: string } {
  if (!(SEGMENT_KEYS as readonly string[]).includes(th.segment)) return { rec: null, skip: 'unknown_segment' };
  const campaigns = v.rows('campaigns').filter((c) => c.theme === th.key);
  if (campaigns.some((c) => OPEN_STATUSES.has(c.status))) return { rec: null, skip: 'already_running' };
  const until = dismissed(v)[th.key];
  if (until && until >= today) return { rec: null, skip: 'dismissed' };
  const lastRun = campaigns.map((c) => (c.completedAt ?? c.launchedAt ?? '').slice(0, 10)).filter(isDay).sort().at(-1) ?? null;
  if (lastRun && diffDays(lastRun, today) < REPEAT_DAYS) return { rec: null, skip: 'recent_run' };
  // channel: the advisor's choice, else the channel with the most consent-eligible people (the theme's first step breaks ties)
  const order: Channel[] = th.sequence[0]?.action === 'call' ? ['phone', ...REC_CHANNELS.filter((c) => c !== 'phone')] : [...REC_CHANNELS];
  // the same computation as previewAudience() (what prepareCampaign freezes), with the segment run once for all channels
  const segment = runSegment(v, th.segment, today);
  const previews = new Map(order.map((ch): [Channel, AudiencePreview] => {
    const entries = audienceFor(v, segment.members.map((m) => m.personId), ch);
    const excluded: Record<string, number> = {};
    for (const e of entries) if (!e.eligible) excluded[e.reason!] = (excluded[e.reason!] ?? 0) + 1;
    return [ch, { segment, entries, eligible: entries.filter((e) => e.eligible).length, excluded }];
  }));
  const byChannel: Partial<Record<Channel, number>> = Object.fromEntries([...previews].map(([ch, p]) => [ch, p.eligible]));
  const channel = opts.channel ?? order.reduce((best, ch) => (previews.get(ch)!.eligible > previews.get(best)!.eligible ? ch : best), order[0]!);
  const pv = previews.get(channel)!;
  if (!pv.eligible) return { rec: null, skip: 'no_eligible' };
  const recent = opts.recent ?? recentlyContacted(v, today);
  const eligibleIds = pv.entries.filter((e) => e.eligible).map((e) => e.personId);
  const recentlyN = eligibleIds.filter((id) => recent.has(id)).length;
  const fresh = eligibleIds.length - recentlyN;
  if (!fresh) return { rec: null, skip: 'all_recently_contacted' };
  const month = Number(today.slice(5, 7));
  const season = SEASONAL_THEMES[th.key]?.months.includes(month) ? SEASONAL_THEMES[th.key]!.reason : null;
  const market = marketNotes(v).filter((n) => n.themes.includes(th.key) && n.source && isDay(n.date) && n.date <= today && diffDays(n.date, today) <= MARKET_NOTE_DAYS)
    .sort((a, b) => b.date.localeCompare(a.date)).slice(0, 3);
  // schedule: first step on the next working day; first contacts spread over at most 5 working days
  const start = nextWorkingDay(today);
  const steps = th.sequence.map((s) => ({ day: addDays(start, s.day), action: s.action }));
  const days = Math.min(5, eligibleIds.length);
  const perDay = Math.ceil(eligibleIds.length / days);
  const contactEnd = workingDaysAfter(start, days - 1);
  const end = [contactEnd, ...steps.map((s) => s.day)].sort().at(-1)!;
  const reasons: RecReason[] = [
    { key: 'segment', params: { segment: th.segment, members: pv.segment.members.length, population: pv.segment.population } },
    { key: 'consent', params: { eligible: pv.eligible, members: pv.entries.length, excluded: pv.entries.length - pv.eligible } },
  ];
  if (recentlyN) reasons.push({ key: 'cool_off', params: { count: recentlyN, days: COOL_OFF_DAYS } });
  if (lastRun) reasons.push({ key: 'last_run', params: { date: lastRun, days: diffDays(lastRun, today) } });
  else reasons.push({ key: 'never_run', params: {} });
  if (season) reasons.push({ key: `season_${season}`, params: { month } });
  for (const n of market) reasons.push({ key: 'market', params: { title: n.title, source: n.source, date: n.date } });
  if (pv.segment.incomplete.length) reasons.push({ key: 'incomplete', params: { count: pv.segment.incomplete.length } });
  const score = fresh + (season ? 5 + fresh * 0.5 : 0) + market.length * 5 + (lastRun ? 0 : 2);
  return {
    rec: {
      key: `${th.key}:${channel}`, themeKey: th.key, segmentKey: th.segment, channel,
      audience: { members: pv.segment.members.length, eligible: pv.eligible, excluded: pv.excluded, incomplete: pv.segment.incomplete.length, recentlyContacted: recentlyN, fresh, population: pv.segment.population },
      byChannel, contactGoal: { total: pv.eligible, perDay, days }, schedule: { start, end, steps }, draft: draftFor(th, channel, opts.locale ?? 'el'),
      kpis: kpisFor(v, pv.eligible), market, season, reasons, score,
    },
  };
}

/** The 1–3 proposals of the moment (fewer when fewer are justified; none is a valid answer). Deterministic. */
export function recommendCampaigns(v: WorkspaceView, today: Day, opts: { locale?: string; limit?: number } = {}): CampaignRecommendation[] {
  const limit = Math.max(1, Math.min(3, opts.limit ?? 3));
  const recent = recentlyContacted(v, today);
  const all = CAMPAIGN_THEMES.map((th) => evaluateTheme(v, th, today, { locale: opts.locale, recent }).rec).filter((r): r is CampaignRecommendation => !!r)
    .sort((a, b) => b.score - a.score || a.themeKey.localeCompare(b.themeKey));
  const out: CampaignRecommendation[] = [];
  const segs = new Set<string>();
  for (const r of all) { if (segs.has(r.segmentKey)) continue; segs.add(r.segmentKey); out.push(r); if (out.length >= limit) break; }
  return out;
}

/** a view that sees the rows of a changeset not yet written (to chain save → prepare in one changeset) */
function overlay(v: WorkspaceView, puts: Put[]): WorkspaceView {
  const extra = <T extends TableName>(t: T) => puts.filter((p) => p.table === t).map((p) => p.row as RowTypes[T]);
  const ids = (t: TableName) => new Set(extra(t).map((r) => (r as { id: string }).id));
  return {
    rows: (t) => { const add = extra(t); const s = ids(t); return [...v.rows(t).filter((r) => !s.has((r as { id: string }).id)), ...add] as never; },
    get: (t, id) => (extra(t).find((r) => (r as { id: string }).id === id) as never) ?? v.get(t, id),
    where: (t, f, val) => { const add = extra(t).filter((r) => (r as unknown as Record<string, unknown>)[f as string] === val); const s = ids(t); return [...v.where(t, f, val).filter((r) => !s.has((r as { id: string }).id)), ...add] as never; },
  };
}

export interface ApproveInput {
  themeKey: string; channel: Channel; locale: string;
  /** the advisor's edits (title, message) — the draft is only a starting point */
  title?: string; message?: { subject?: string; body: string };
  /** must be literally true: the advisor ticked «I approve» on the screen */
  approved: boolean;
  /** what the advisor saw (stored with the campaign as the record of the proposal) */
  shown?: Pick<CampaignRecommendation, 'audience' | 'kpis' | 'schedule' | 'contactGoal' | 'reasons'> | null;
}
/**
 * Approval: creates the campaign (draft) and freezes its consent-checked audience with prepareCampaign, in one
 * changeset. Nothing is sent and no task is created — the advisor launches it on the campaign when ready.
 */
export function approveRecommendation(v: WorkspaceView, input: ApproveInput, ctx: Ctx): Changeset {
  if (input.approved !== true) throw new DomainError('recommendation.approval_required');
  const th = themeOf(input.themeKey);
  if (!th) throw new DomainError('recommendation.unknown_theme');
  if (!REC_CHANNELS.includes(input.channel)) throw new DomainError('recommendation.channel');
  if (v.rows('campaigns').some((c) => c.theme === th.key && OPEN_STATUSES.has(c.status))) throw new DomainError('recommendation.already_running');
  const body = clean(input.message?.body);
  const message = body ? { ...(input.message?.subject ? { subject: clean(input.message.subject) } : {}), body } : draftFor(th, input.channel, input.locale);
  const save = saveCampaign(v, { title: clean(input.title) || themeText(th, input.locale).title, theme: th.key, segmentKey: th.segment, channel: input.channel, locale: input.locale.startsWith('el') ? 'el' : 'en', message }, ctx);
  const row = save.puts[0]!.row as RowTypes['campaigns'];
  row.criteria = { recommendation: { approvedAt: ctx.now, approvedBy: ctx.actor, inputs: [...RECOMMENDATION_INPUTS], shown: input.shown ?? null } };
  const prep = prepareCampaign(overlay(v, save.puts), row.id, ctx);
  const last = new Map<string, Put>();
  for (const p of [...save.puts, ...prep.puts]) last.set(`${p.table}:${(p.row as { id: string }).id}`, p);
  const frozen = (prep.puts[0]!.row as RowTypes['campaigns']).audience as { eligible?: number } | undefined;
  return { action: 'campaign.recommendation.approve', entity: 'campaigns', entityId: row.id, summary: `${row.title} · ${frozen?.eligible ?? 0}`, puts: [...last.values()], deletes: [] };
}

/** «Not now»: the theme is not proposed again for `days` days (default 30). */
export function dismissRecommendation(v: WorkspaceView, themeKey: string, ctx: Ctx, days = 30): Changeset {
  if (!themeOf(themeKey)) throw new DomainError('recommendation.unknown_theme');
  const cur = dismissed(v);
  const next = Object.fromEntries(Object.entries(cur).filter(([, d]) => d >= ctx.today));
  next[themeKey] = addDays(ctx.today, Math.max(1, Math.min(365, Math.round(days))));
  return { ...saveSetting(v, DISMISSED_KEY, next, ctx), action: 'campaign.recommendation.dismiss', summary: themeKey };
}
